import { isDevHubClient } from '../../environment/devHub';
import { getSubscriptionMenuProbeModeSync } from '../../flags/subscriptionMenuProbe';
import type {
  SubscriptionMenuProbeLogCategory,
  SubscriptionMenuProbeLogEntry,
  SubscriptionMenuProbeMode,
} from './types';

const MAX_LOG_ENTRIES = 600;
const log_entries: SubscriptionMenuProbeLogEntry[] = [];

function create_log_id(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function subscriptionMenuProbeLoggingActive(): boolean {
  if (!isDevHubClient()) {
    return false;
  }
  return getSubscriptionMenuProbeModeSync() !== 'disabled';
}

export function appendSubscriptionMenuProbeLog(input: {
  mode?: SubscriptionMenuProbeMode;
  category: SubscriptionMenuProbeLogCategory;
  label: string;
  request?: Record<string, unknown>;
  response?: unknown;
  error?: string;
  notes?: string;
}): void {
  if (!subscriptionMenuProbeLoggingActive()) {
    return;
  }
  const mode = input.mode ?? getSubscriptionMenuProbeModeSync();
  const entry: SubscriptionMenuProbeLogEntry = {
    id: create_log_id(),
    at: new Date().toISOString(),
    mode,
    category: input.category,
    label: input.label,
    request: input.request,
    response: input.response,
    error: input.error,
    notes: input.notes,
  };
  log_entries.unshift(entry);
  if (log_entries.length > MAX_LOG_ENTRIES) {
    log_entries.length = MAX_LOG_ENTRIES;
  }
}

export function getSubscriptionMenuProbeLogs(): SubscriptionMenuProbeLogEntry[] {
  return [...log_entries];
}

export function getSubscriptionMenuProbeLogCount(): number {
  return log_entries.length;
}

export function clearSubscriptionMenuProbeLogs(): void {
  log_entries.length = 0;
}

export function exportSubscriptionMenuProbeLogsJson(): string {
  const payload = {
    exported_at: new Date().toISOString(),
    mode: getSubscriptionMenuProbeModeSync(),
    entry_count: log_entries.length,
    entries: log_entries,
  };
  return JSON.stringify(payload, null, 2);
}

export function exportSubscriptionMenuProbeLogsText(): string {
  const lines: string[] = [
    `Subscription menu probe log`,
    `Exported: ${new Date().toISOString()}`,
    `Mode: ${getSubscriptionMenuProbeModeSync()}`,
    `Entries: ${log_entries.length}`,
    '',
  ];
  for (const entry of log_entries) {
    lines.push(`--- ${entry.at} [${entry.category}] ${entry.label} ---`);
    lines.push(`mode: ${entry.mode}`);
    if (entry.notes) {
      lines.push(`notes: ${entry.notes}`);
    }
    if (entry.request) {
      lines.push(`request: ${JSON.stringify(entry.request)}`);
    }
    if (entry.error) {
      lines.push(`error: ${entry.error}`);
    }
    if (entry.response !== undefined) {
      lines.push(`response: ${JSON.stringify(entry.response)}`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

const SUBSCRIPTION_PATH_HINTS = [
  'customer_subscriptions',
  'my-customer-subscriptions',
  'subscription/',
];

const MENU_PATH_HINTS = ['menu_builders', 'menu_subscriptions', 'menu_items', 'select/menu_items', 'clear/menu_items'];

function path_matches(path: string, hints: string[]): boolean {
  const normalized = path.toLowerCase();
  return hints.some(h => normalized.includes(h));
}

/** Axios interceptor hook — captures subscription + menu API traffic when probe is on. */
export function captureSubscriptionMenuProbeApiCall(input: {
  method: string;
  path: string;
  params?: unknown;
  body?: unknown;
  response?: unknown;
  error?: unknown;
  status?: number;
}): void {
  if (!subscriptionMenuProbeLoggingActive()) {
    return;
  }
  const path = input.path ?? '';
  let category: SubscriptionMenuProbeLogCategory | null = null;
  if (path_matches(path, SUBSCRIPTION_PATH_HINTS)) {
    category = 'subscription_fetch';
  } else if (path_matches(path, MENU_PATH_HINTS)) {
    category = input.method === 'POST' ? 'menu_book' : 'menu_fetch';
  }
  if (!category) {
    return;
  }

  const request: Record<string, unknown> = {
    method: input.method,
    path,
  };
  if (input.params != null) {
    request.params = input.params;
  }
  if (input.body != null) {
    request.body = input.body;
  }
  if (input.status != null) {
    request.http_status = input.status;
  }

  appendSubscriptionMenuProbeLog({
    category,
    label: `${input.method} ${path}`,
    request,
    response: input.response,
    error: input.error instanceof Error ? input.error.message : input.error != null ? String(input.error) : undefined,
  });
}
